import React, { createContext, useContext, useState } from "react";

/**
 * The ONE storage key for the detail drawer's dragged width. The drag is
 * authored app-side (`detail-view-popup.tsx` mounts the resize seam), so the
 * read and write helpers live here together and the key cannot drift
 * between them.
 */
const SIDEBAR_WIDTH_KEY = "__dialog-sidebar-width";

// Task-drawer bounds: a fixed 400px floor and a ceiling of 65% of the
// window. The floor is the narrowest the properties band and the activity
// feed still read at; the ceiling keeps a strip of the page beside the
// drawer in view.
const SIDEBAR_MIN_PX = 400;
const SIDEBAR_MAX_RATIO = 0.65;
// The width a never-dragged drawer opens at — the old floor, so lowering the
// floor does not narrow the drawer for anyone who never resized it.
const SIDEBAR_DEFAULT_PX = 520;

/** Drag bounds, resolved against the live viewport — the sheet is
 *  viewport-anchored, so the viewport width IS the container width. */
export function sidebarWidthBounds(containerWidth: number): {
  min: number;
  max: number;
} {
  // On a window below ~615px, 65% falls under the floor — never let the
  // bounds invert; the drawer is simply fixed-width there.
  const max = Math.max(SIDEBAR_MIN_PX, containerWidth * SIDEBAR_MAX_RATIO);
  return { min: SIDEBAR_MIN_PX, max };
}

/** The persisted width clamped to the current viewport, or the default —
 *  never dragged, the drawer opens at SIDEBAR_DEFAULT_PX. */
export function readSidebarWidth(containerWidth: number): number {
  const { min, max } = sidebarWidthBounds(containerWidth);
  let saved = 0;
  try {
    saved = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY)) || 0;
  } catch {
    /* localStorage unavailable */
  }
  const width =
    Number.isFinite(saved) && saved > 0 ? saved : SIDEBAR_DEFAULT_PX;
  return Math.min(Math.max(width, min), max);
}

export function writeSidebarWidth(width: number): void {
  try {
    window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
  } catch {
    /* localStorage unavailable */
  }
}

const DetailDrawerWidthContext = createContext<number>(0);
const ReportDetailDrawerWidthContext = createContext<(width: number) => void>(
  () => {},
);

/**
 * The rendered width of the open detail drawer, published by the drawer
 * itself and read by the page beneath it. The map insets its top-right
 * action cluster and offsets its camera by this number, so it has to be the
 * width the drawer actually paints — the stored preference is clamped to the
 * window's bounds at open and streamed live through a drag, and the stored
 * value alone matches neither.
 */
export function DetailDrawerWidthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // The state setter is referentially stable, so it IS the report callback.
  const [width, setWidth] = useState(0);
  return (
    <ReportDetailDrawerWidthContext.Provider value={setWidth}>
      <DetailDrawerWidthContext.Provider value={width}>
        {children}
      </DetailDrawerWidthContext.Provider>
    </ReportDetailDrawerWidthContext.Provider>
  );
}

/** Width in px of the open detail drawer overlaying the page; 0 while no
 *  drawer is open (a fullscreen detail view is a modal, not a drawer). */
export function useDetailDrawerWidth(): number {
  return useContext(DetailDrawerWidthContext);
}

/** The drawer's side of the contract: the popup calls this with its live
 *  width on open and every drag frame, and with 0 when it closes. */
export function useReportDetailDrawerWidth(): (width: number) => void {
  return useContext(ReportDetailDrawerWidthContext);
}
